/**
 * Product Swatches
 * @module productSwatches
 */

import $ from 'jquery';
import debounce from 'lodash/debounce';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';
export const PLUGIN_NAME = 'productSwatches';

export const DEFAULTS = {
  productSwatch: '.product-swatches__swatch',
  viewMoreBtnBlock: '.js-toggle-block',
  viewMoreBtn: '.js-toggle-row',
  visibleRows: 2,
  radioSelector: '.product-swatches__radio'
};

/**
 * Checks if the top of the last swatch is >= the height of the swatch
 * multiplied by the # of expected rows
 * @param  {jQuery} $el - product swatches element
 * @param  {jQuery} $lastSwatch - last swatch nested in the product swatches element
 * @return {Boolean}
 */
const lastSwatchIsHidden = ($el, $lastSwatch, opts) => {
  if($lastSwatch.position().top >= ($lastSwatch.outerHeight(true) * opts.visibleRows)) {
    return true;
  }
  return false;
};

/**
 * Toggle View More button
 * @param  {jQuery} $el - product swatches element
 * @param  {jQuery} $lastSwatch - last swatch nested in the product swatches element
 */
const toggleViewMore = ($el, $lastSwatch, opts) => {
  const { viewMoreBtn, viewMoreBtnBlock } = opts;
  if(lastSwatchIsHidden($el, $lastSwatch, opts)) {
    $el.next(viewMoreBtnBlock).find(viewMoreBtn).removeClass('u-hide');
  } else {
    $el.next(viewMoreBtnBlock).find(viewMoreBtn).addClass('u-hide');
  }
};
/**
 * Initialize keyboard navigation for radio buttons
 * @param  {jQuery} $el - product swatches element
 * @param  {Object} opts - options
 */
const initKeyboardNavigation = ($el, opts) => {
  const $swatches = $el.find(opts.radioSelector);
  
  // Set initial tabindex
  $swatches.each(function() {
    $(this).attr('tabindex', $(this).prop('checked') ? '0' : '-1');
  });
  
  // Arrow key navigation - attach directly to each radio button
  $swatches.on('keydown', function(e) {
    const $allSwatches = $el.find(opts.radioSelector); // Re-query to ensure we have all
    const currentIndex = $allSwatches.index($(this));
    
    let newIndex = currentIndex;
    const count = $allSwatches.length;
    
    switch(e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        newIndex = (currentIndex + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        newIndex = (currentIndex - 1 + count) % count;
        break;
      default:
        return;
    }
    
    $allSwatches.attr('tabindex', '-1');
    $allSwatches.eq(newIndex).attr('tabindex', '0').prop('checked', true).focus().trigger('change');
  });
  
  // Handle clicks to update tabindex
  $swatches.on('change', function() {
    const $allSwatches = $el.find(opts.radioSelector);
    $allSwatches.attr('tabindex', '-1');
    $(this).attr('tabindex', '0');
  });
};

/**
 * Set Event Listener
 * @param  {jQuery} $el - header element
 * @param  {jQuery} $window - window element
 */
const setEventListener = ($el, $window, opts) => {
  const $lastSwatch = $el.find(opts.productSwatch).last();
  $window.on('resize.productSwatch', debounce(() => { toggleViewMore($el, $lastSwatch, opts); }, 100)).trigger('resize.productSwatch');
};


/**
 * Initializes product swatches
 * @param {HTMLElement} el - product swatches element
 * @see DEFAULTS
 */
export default function productSwatches(el, options = {}) {
  const $el = $(el);
  const $window = $(window);
  const opts = mergeOptions(DEFAULTS, options, $el);

  initKeyboardNavigation($el, opts); 
  setEventListener($el, $window, opts);
}
registerJQueryPlugin(PLUGIN_NAME, productSwatches);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/product-swatches.js